// @givmo/app-charity — the portal's ONE sign-out.
//
// Shared by both chromes (`components/app-shell.tsx` and `components/onboarding-shell.tsx`) so an
// admin who has not claimed an organization yet leaves through exactly the same path as a claimed
// Owner. The `queryClient.clear()` is the load-bearing part: every charity read here is self-scoped,
// so without it the NEXT account to sign in on this device paints the previous one's memberships and
// claims until its own reads resolve — which for this app decides which portal it even renders.

import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from '@givmo/design-system';
import { clearLastModules } from '@givmo/org-console';
import { useAuth } from './auth-context';

export interface SignOutState {
  /** Fire-and-forget sign-out; safe to call repeatedly (re-entry is guarded). */
  signOut: () => void;
  /** True from the first click until the sign-out lands or fails. */
  isSigningOut: boolean;
}

export function useSignOut(): SignOutState {
  const { user, signOut } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const pendingRef = useRef(false);
  const uid = user?.uid ?? null;

  const run = useCallback(async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setIsSigningOut(true);
    try {
      // The last-module hint is this person's, on this device — it goes with their session (shell
      // contract v3, T1.5).
      clearLastModules(uid);
      await signOut();
      queryClient.clear();
      await navigate({ to: '/login-home', replace: true });
      // ⚠ Twice, deliberately. The authenticated tree is only unmounted once the navigation lands, so
      // between the first clear and that unmount any still-mounted observer re-registers its (now
      // empty) query — React Query batches removal notifications, so WHICH observers win that race is
      // timing, not design. The second clear runs with the tree gone and leaves the cache genuinely
      // empty instead of holding orphaned placeholders. The first clear is not redundant: it is what
      // drops the previous user's DATA at the earliest possible moment.
      queryClient.clear();
    } catch {
      toast.error('We couldn’t sign you out. Please try again.');
      pendingRef.current = false;
      setIsSigningOut(false);
    }
  }, [navigate, queryClient, signOut, uid]);

  return { signOut: () => void run(), isSigningOut };
}
